// touchGesture — M1-c タッチジェスチャ (猶予窓 + 2本指パン/ピンチ) の pure helper
// (docs/_wip/modern-phone-shell-2026-07-02.md §2 確定B案 / §3.1 M1-c)。
//
// ## 背景・経緯
//
// スマホ (指のみ) の目標挙動は「1本指 = 描画 / 2本指 = パン + ピンチズーム」。
// ところが stroke.start は即送信 (protocol に cancel が無い) ため、「2本目の指で
// 進行中ストロークを消す」を厳密にやると protocol/server/engine の新設が要る。
// consult 2026-07-02 で **猶予窓方式 (B案)** に確定:
//
//   - `pointerType === 'touch'` のみ、pointerdown から `TOUCH_GRACE_WINDOW_MS`
//     の間はストローク開始を保留し、点列をローカルバッファに溜める。
//   - 窓内に2本目の touch が着地 → バッファ破棄 (stroke.start 未送信なので
//     protocol 完全無影響) → 2本指ジェスチャモード (パン + ピンチ) へ。
//   - 窓が経過 or 窓内に pointerup (= 短タップ) → バッファの点列からストローク
//     開始/確定。
//   - 窓を逃した後着の2本目は現行どおり黙殺 (ストローク続行)。
//   - pen / mouse はこの経路を一切通らない (= 即時開始のまま、描き味不変)。
//
// ## なぜ pure helper として外出ししたか
//
// `packages/web` の vitest は node env, no DOM (`vitest.config.ts`)。React
// component や PointerEvent を node 環境で組み立てられないため、状態機械の遷移
// とジェスチャ幾何 (重心・距離比 → pan delta / nextZoom) を pure 関数に切り出して
// 単体テスト可能にする (`penOnlyMode.ts` / `spuit.ts` と同じ流儀)。
// `DrawingCanvas.tsx` 側の impure shell (タイマー・listener・engine 呼び出し) は
// この reducer が返す effects を実行するだけにする。

/**
 * 猶予窓の長さ (ms)。touch の pointerdown からこの時間はストローク開始を保留する
 * (= touch のみ描き出しがこのぶん遅れる)。~50ms は consult 2026-07-02 の仮値で、
 * 実機フィードバックで調整する前提 (modern-phone-shell §4 要検証)。
 */
export const TOUCH_GRACE_WINDOW_MS = 50;

/**
 * touch 入力の状態機械のフェーズ。
 *
 * - `idle`     … touch 操作なし。
 * - `pending`  … 1本目 touch が着地、猶予窓が走行中 (点列はバッファに蓄積)。
 * - `stroking` … 窓が経過してバッファからストロークを開始済 (= activeRef の
 *                通常 stroke 経路が処理中。ここでは後着 touch の黙殺と、確定時の
 *                idle 復帰だけを追う)。
 * - `gesture`  … 2本指ジェスチャ (パン + ピンチ)。`pointerIds` = 追跡中の指。
 *                全指が離れたら解除。第3指以降は追跡しない (= 黙殺)。
 */
export type TouchGesturePhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending'; readonly pointerId: number }
  | { readonly kind: 'stroking'; readonly pointerId: number }
  | { readonly kind: 'gesture'; readonly pointerIds: readonly number[] };

/** 初期フェーズ (共有定数。immutable なので使い回して良い)。 */
export const IDLE_TOUCH_PHASE: TouchGesturePhase = { kind: 'idle' };

/**
 * 状態機械への入力イベント。すべて touch pointer 由来 (pen / mouse は shell 側で
 * この機械に到達させない)。
 */
export type TouchGestureEvent =
  | { readonly type: 'touch-down'; readonly pointerId: number }
  | { readonly type: 'touch-up'; readonly pointerId: number }
  | { readonly type: 'touch-cancel'; readonly pointerId: number }
  | { readonly type: 'window-elapsed' };

/**
 * impure shell (DrawingCanvas) が実行すべき副作用。
 *
 * - `start-window`   … 猶予タイマー開始。
 * - `cancel-window`  … 猶予タイマー破棄。
 * - `begin-stroke`   … バッファ点列からストローク開始 (stroke.start 送信はこの時点)。
 * - `commit-tap`     … バッファからストローク開始 + 即確定 (窓内 pointerup の
 *                      短タップも1点ストロークとして成立させる)。
 * - `discard-buffer` … バッファ破棄 (stroke.start 未送信のまま = protocol 無影響)。
 * - `enter-gesture`  … 2本指ジェスチャモードへ (pan/pinch 追跡開始)。
 * - `exit-gesture`   … 全指離脱でジェスチャ解除 (追跡 listener 撤去)。
 */
export type TouchGestureEffect =
  | 'start-window'
  | 'cancel-window'
  | 'begin-stroke'
  | 'commit-tap'
  | 'discard-buffer'
  | 'enter-gesture'
  | 'exit-gesture';

export interface TouchGestureTransition {
  readonly phase: TouchGesturePhase;
  readonly effects: readonly TouchGestureEffect[];
}

const stay = (phase: TouchGesturePhase): TouchGestureTransition => ({ phase, effects: [] });

/**
 * 猶予窓 + 2本指ジェスチャの状態遷移 (pure)。
 *
 * 遷移表:
 *
 * | phase    | event                  | next                | effects |
 * |----------|------------------------|---------------------|---------|
 * | idle     | touch-down(a)          | pending(a)          | start-window |
 * | pending(a) | touch-down(b≠a)      | gesture([a,b])      | cancel-window, enter-gesture, discard-buffer |
 * | pending(a) | window-elapsed       | stroking(a)         | begin-stroke |
 * | pending(a) | touch-up(a)          | idle                | cancel-window, commit-tap |
 * | pending(a) | touch-cancel(a)      | idle                | cancel-window, discard-buffer |
 * | stroking(a) | touch-down(b)       | stroking(a)         | (黙殺 = 窓後の後着は現行どおり) |
 * | stroking(a) | touch-up/cancel(a)  | idle                | (flush/end は activeRef 経路が担う) |
 * | gesture  | touch-down(c)          | gesture (不変)      | (第3指は追跡しない = 黙殺) |
 * | gesture  | touch-up/cancel(∈ids)  | 残あり: gesture(残) | |
 * |          |                        | 残なし: idle        | exit-gesture |
 *
 * その他 (無関係 pointer の up/cancel、idle への余剰イベント等) は現状維持 +
 * effects なし。
 *
 * effects の順序は shell の実行順序として意味を持つ:
 * `enter-gesture` は pending バッファの「1本目の最終位置」を読むため、
 * `discard-buffer` (= バッファ破棄) より **先** に並べる。
 */
export const transitionTouchGesture = (
  phase: TouchGesturePhase,
  event: TouchGestureEvent,
): TouchGestureTransition => {
  switch (phase.kind) {
    case 'idle': {
      if (event.type === 'touch-down') {
        return {
          phase: { kind: 'pending', pointerId: event.pointerId },
          effects: ['start-window'],
        };
      }
      return stay(phase);
    }
    case 'pending': {
      switch (event.type) {
        case 'touch-down': {
          if (event.pointerId === phase.pointerId) return stay(phase); // defensive (再 down は来ない)
          return {
            phase: { kind: 'gesture', pointerIds: [phase.pointerId, event.pointerId] },
            effects: ['cancel-window', 'enter-gesture', 'discard-buffer'],
          };
        }
        case 'window-elapsed':
          return {
            phase: { kind: 'stroking', pointerId: phase.pointerId },
            effects: ['begin-stroke'],
          };
        case 'touch-up': {
          if (event.pointerId !== phase.pointerId) return stay(phase);
          return { phase: IDLE_TOUCH_PHASE, effects: ['cancel-window', 'commit-tap'] };
        }
        case 'touch-cancel': {
          if (event.pointerId !== phase.pointerId) return stay(phase);
          return { phase: IDLE_TOUCH_PHASE, effects: ['cancel-window', 'discard-buffer'] };
        }
      }
      return stay(phase);
    }
    case 'stroking': {
      if (
        (event.type === 'touch-up' || event.type === 'touch-cancel') &&
        event.pointerId === phase.pointerId
      ) {
        // 実際の flush + stroke.end は activeRef の通常 stroke 経路が担うので
        // ここは idle への復帰だけ (effects なし)。
        return stay(IDLE_TOUCH_PHASE);
      }
      // 窓後の後着 touch-down は黙殺 (ストローク続行、現行挙動維持)。
      return stay(phase);
    }
    case 'gesture': {
      if (event.type === 'touch-up' || event.type === 'touch-cancel') {
        if (!phase.pointerIds.includes(event.pointerId)) return stay(phase);
        const remaining = phase.pointerIds.filter((id) => id !== event.pointerId);
        if (remaining.length === 0) {
          return { phase: IDLE_TOUCH_PHASE, effects: ['exit-gesture'] };
        }
        return stay({ kind: 'gesture', pointerIds: remaining });
      }
      // 第3指以降の touch-down は追跡しない (= 黙殺、ジェスチャは最初の2本で継続)。
      return stay(phase);
    }
  }
};

// ---------------------------------------------------------------------------
// penOnlyMode 中の touch 状態機械 (決定1、consult 2026-09-29、
// modern-phone-shell-2026-07-02.md §6)
// ---------------------------------------------------------------------------

/**
 * penOnlyMode (Palm Rejection、`penOnlyMode.ts`) ON 中の touch 専用の状態機械
 * (consult 2026-09-29、§6 決定1)。
 *
 * penOnlyMode 中の touch は**ストロークを一切開始しない**方針 (1本指は
 * palm rejection のため完全に無視、2本指だけが意味を持つ = ジェスチャ)。
 * 上の `TouchGesturePhase` / `transitionTouchGesture` (= 非 penOnly 経路、
 * 猶予窓 + ストローク開始) とは目的が異なるため、専用の小さな状態機械として
 * 分離する。猶予窓のタイマーも不要 (1本指が「描画になるかもしれない」猶予を
 * 置く必要が無く、2本目が来たら即ジェスチャで良いため) — `TouchGestureEffect`
 * の `start-window`/`cancel-window`/`begin-stroke`/`commit-tap` に相当するものは
 * 無い。既存 `transitionTouchGesture` は本追加で一切変更していない
 * (= penOnlyMode OFF の既存経路は完全に非退行)。
 *
 * - `idle`     … touch 操作なし。
 * - `watching` … 1本目 touch が着地、無視して2本目を待つだけ ( = palm
 *                rejection、「1本指は何もしない」の一部として pointermove は
 *                追わない — 着地位置のまま2本目を待つ)。
 * - `gesture`  … 2本指ジェスチャ (パン + ピンチ)。既存 `TouchGesturePhase`
 *                の `gesture` と同じ意味・同じ幾何 (`computeGestureFrame` 等を
 *                共用、impure shell 側で `touchGestureRef` セッションも共用する)。
 */
export type PenOnlyTouchPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'watching'; readonly pointerId: number; readonly lastClient: GesturePoint }
  | { readonly kind: 'gesture'; readonly pointerIds: readonly number[] };

/** 初期フェーズ (共有定数。immutable なので使い回して良い)。 */
export const IDLE_PEN_ONLY_TOUCH_PHASE: PenOnlyTouchPhase = { kind: 'idle' };

/** 状態機械への入力イベント。penOnlyMode 中の touch pointer 由来のみ。 */
export type PenOnlyTouchEvent =
  | { readonly type: 'touch-down'; readonly pointerId: number; readonly client: GesturePoint }
  | { readonly type: 'touch-up'; readonly pointerId: number }
  | { readonly type: 'touch-cancel'; readonly pointerId: number };

/**
 * impure shell が実行すべき副作用。猶予窓が無いので `TouchGestureEffect` の
 * ような開始系 effect は無い。
 *
 * - `enter-gesture` … 2本指ジェスチャモードへ (`watching` の1本目位置 + 今回の
 *   イベントの2本目位置で session 初期化。既存 `TouchGestureEffect` の
 *   `enter-gesture` と同じ実体 (`enterGesture`) を共用する)。
 * - `exit-gesture`  … 全指離脱でジェスチャ解除 (既存 `teardownGesture` を共用)。
 */
export type PenOnlyTouchEffect = 'enter-gesture' | 'exit-gesture';

export interface PenOnlyTouchTransition {
  readonly phase: PenOnlyTouchPhase;
  readonly effects: readonly PenOnlyTouchEffect[];
}

const stayPenOnly = (phase: PenOnlyTouchPhase): PenOnlyTouchTransition => ({ phase, effects: [] });

/**
 * penOnlyMode 中の touch 状態遷移 (pure)。
 *
 * 遷移表:
 *
 * | phase       | event               | next                | effects |
 * |-------------|---------------------|----------------------|---------|
 * | idle        | touch-down(a)       | watching(a)          | (無し。1本指は無視するだけ) |
 * | watching(a) | touch-down(b≠a)     | gesture([a,b])       | enter-gesture |
 * | watching(a) | touch-up/cancel(a)  | idle                 | (無し。ストローク不成立のまま消える) |
 * | gesture     | touch-up/cancel     | 残あり: gesture(残) / 残なし: idle | 残なし時のみ exit-gesture |
 * | gesture     | touch-down(第3指)   | gesture (不変)       | (黙殺、既存 gesture と同じ方針) |
 *
 * その他 (無関係 pointer の up/cancel、同一 pointer の再 down 等) は現状維持 +
 * effects なし。
 */
export const transitionPenOnlyTouch = (
  phase: PenOnlyTouchPhase,
  event: PenOnlyTouchEvent,
): PenOnlyTouchTransition => {
  switch (phase.kind) {
    case 'idle': {
      if (event.type === 'touch-down') {
        return stayPenOnly({
          kind: 'watching',
          pointerId: event.pointerId,
          lastClient: event.client,
        });
      }
      return stayPenOnly(phase);
    }
    case 'watching': {
      switch (event.type) {
        case 'touch-down': {
          if (event.pointerId === phase.pointerId) return stayPenOnly(phase); // defensive (再 down は来ない)
          return {
            phase: { kind: 'gesture', pointerIds: [phase.pointerId, event.pointerId] },
            effects: ['enter-gesture'],
          };
        }
        case 'touch-up':
        case 'touch-cancel': {
          if (event.pointerId !== phase.pointerId) return stayPenOnly(phase);
          return stayPenOnly(IDLE_PEN_ONLY_TOUCH_PHASE);
        }
      }
      return stayPenOnly(phase);
    }
    case 'gesture': {
      if (event.type === 'touch-up' || event.type === 'touch-cancel') {
        if (!phase.pointerIds.includes(event.pointerId)) return stayPenOnly(phase);
        const remaining = phase.pointerIds.filter((id) => id !== event.pointerId);
        if (remaining.length === 0) {
          return { phase: IDLE_PEN_ONLY_TOUCH_PHASE, effects: ['exit-gesture'] };
        }
        return stayPenOnly({ kind: 'gesture', pointerIds: remaining });
      }
      // 第3指以降の touch-down は追跡しない (黙殺、既存 gesture と同じ方針)。
      return stayPenOnly(phase);
    }
  }
};

// ---------------------------------------------------------------------------
// ジェスチャ幾何 (2本指の重心・距離比 → pan delta / nextZoom)
// ---------------------------------------------------------------------------

/** client 座標系の点 (PointerEvent.clientX/Y)。 */
export interface GesturePoint {
  readonly x: number;
  readonly y: number;
}

/** 2点の重心 (= 中点)。パンの追跡点 + ピンチのアンカー。 */
export const gestureCentroid = (a: GesturePoint, b: GesturePoint): GesturePoint => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});

/** 2点間距離。ピンチの倍率算出に使う。 */
export const gestureDistance = (a: GesturePoint, b: GesturePoint): number =>
  Math.hypot(b.x - a.x, b.y - a.y);

/**
 * 1フレームぶんのジェスチャ適用量。
 *
 * - `centroid` … 現在の2指重心 (次フレームの lastCentroid に持ち越す)。
 * - `panDx/panDy` … 前フレーム重心からの移動量 (client px)。shell は
 *   `tx += panDx` (viewport.ts の `{tx,ty}`、2026-09-29 決定5) で「指に内容が
 *   追従する」方向に適用する。
 * - `nextZoom` … ジェスチャ開始時 zoom × 距離比 (**絶対値**)。増分でなく
 *   開始基準の絶対倍率にすることでフレーム毎の誤差蓄積 (ドリフト) を防ぐ。
 *   clamp は zoom API (`handleZoomToAtPointer` → `clampZoom`) 側に任せる。
 */
export interface GestureFrame {
  readonly centroid: GesturePoint;
  readonly panDx: number;
  readonly panDy: number;
  readonly nextZoom: number;
}

/**
 * 2指の現在位置から今フレームの pan delta と絶対 zoom を計算する (pure)。
 *
 * `startDistance <= 0` (= 2指が同一点に着地した縮退ケース) では距離比が定義
 * できないので zoom は `startZoom` のまま (= パンのみ有効)。
 */
export const computeGestureFrame = (
  a: GesturePoint,
  b: GesturePoint,
  lastCentroid: GesturePoint,
  startZoom: number,
  startDistance: number,
): GestureFrame => {
  const centroid = gestureCentroid(a, b);
  const distance = gestureDistance(a, b);
  const nextZoom = startDistance > 0 ? startZoom * (distance / startDistance) : startZoom;
  return {
    centroid,
    panDx: centroid.x - lastCentroid.x,
    panDy: centroid.y - lastCentroid.y,
    nextZoom,
  };
};

// ---------------------------------------------------------------------------
// 計測: 猶予窓を逃した 2 本目 (late) の判定 (modern-phone-shell §7)
// ---------------------------------------------------------------------------

/** 1 本目の pointerdown 記録 (shell が touch pointerdown 時に作る)。 */
export interface TouchFirstDown {
  readonly pointerId: number;
  /** 1 本目の pointerdown の event.timeStamp (ms)。 */
  readonly timeStamp: number;
  /** この 1 本目のストロークで late を既に報告したか (1 ストローク 1 回)。 */
  readonly lateReported: boolean;
}

/**
 * `stroking` 中に別の touch が着地して黙殺されたとき、late を送るべきか判定する
 * (pure)。送るなら 1 本目 → 2 本目の ms (丸め + 0..max clamp は呼び側の
 * `clampTouchGestureDelayMs`)、送らないなら null。
 *
 * reducer (`transitionTouchGesture`) の遷移・effects は変えないため別関数。
 * 送る条件: phase が stroking / 着地 pointer が 1 本目と別 / 1 本目の記録があり
 * pointerId が phase と一致 / 未報告。
 */
export const lateTouchDelayRaw = (
  phase: TouchGesturePhase,
  downPointerId: number,
  downTimeStamp: number,
  first: TouchFirstDown | null,
): number | null => {
  if (phase.kind !== 'stroking') return null;
  if (downPointerId === phase.pointerId) return null;
  if (first === null || first.pointerId !== phase.pointerId || first.lateReported) return null;
  return downTimeStamp - first.timeStamp;
};
